import gql from 'graphql-tag';
import apolloClient from '../apollo';
import Vue from 'vue';

/**
 * State
 */
const defaultItem = () => {
  return {
    _id: '_new',
    number: '_new',
    pilatNr: '',
    wholesaleNr: '',
    industryNr: '',
    supplier: '',
    configurationState: 'NO',
    description: '',
    unit: 'PIECE',
    type: 'STANDARD',
    solarPlant: 'NO',
    price: 0,
    installationTimeInMinutes: 0,
    addRemoveCount: 1,
    deleted: false,
    itemAttachments: [],
    battery: {
      manufacturer: '',
      type: '',
      capacity: '',
      dischargePower: '',
      batteryModuleCount: '',
      batteryModuleCapacity: '',
      usableCapacity: '',
      ratedVoltage: '',
      connectionType: null,
    },
    inverter: {
      manufacturer: '',
      type: '',
      nominalPower: '',
      apparentPower: '',
      activePower: '',
      ratedCurrent: '',
      shortCircuitCurrent: '',
      nominalDcPower: '',
      allowedChargeDischargeCurrent: '',
      gridAndPlantProtectionIntegrated: '',
      gridAndPlantProtectionType: null,
      nominalVoltage: '',
    },
    energyFlowDirectionSensor: {
      manufacturer: '',
      type: '',
    },
    wallbox: {
      manufacturer: '',
      type: '',
      maxNetInputPower: '',
      maxNetOutputPower: '',
      minChargingPower: '',
      maxChargingPower: '',
      activePowerControllable: null,
      chargingType: null,
      shiftFactorCosphiType: null,
      connectionType: null,
      connectionTypes: [],
    },
    solarPanel: {
      manufacturer: '',
      type: '',
      power: '',
    },
  };
};

const getDefaultState = () => {
  return {
    items: [],
    itemUnits: [],
    itemCount: 0,
    solarPlantItems: [],
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for an item list
 * used for all queries/mutations which return a list of items
 * @type {DocumentNode}
 */
const ITEM_LIST_REQUEST_FIELDS = gql`
  fragment itemListFields on Item {
    _id
    number
    nextItemNumber
    previousItemNumber
    pilatNr
    wholesaleNr
    industryNr
    supplier
    configurationState
    description
    unit
    type
    solarPlant
    price
    installationTimeInMinutes
    createdAt
    updatedAt
    deleted
    priceCalculation {
      # Item pricing
      itemMarkup
      itemProfitMarkup
      itemCustomerPrice

      # Labor pricing
      laborCost
      laborMarkup
      laborProfitMarkup
      laborCustomerPrice

      # Total pricing
      totalCustomerPrice

      calculatedAt
    }
    battery {
      manufacturer
      type
      capacity
      dischargePower
      batteryModuleCount
      batteryModuleCapacity
      usableCapacity
      ratedVoltage
      connectionType
    }
    inverter {
      number
      manufacturer
      type
      nominalPower
      apparentPower
      activePower
      ratedCurrent
      shortCircuitCurrent
      nominalDcPower
      allowedChargeDischargeCurrent
      gridAndPlantProtectionIntegrated
      gridAndPlantProtectionType
      nominalVoltage
    }
    energyFlowDirectionSensor {
      number
      manufacturer
      type
    }
    wallbox {
      manufacturer
      type
    }
    solarPanel {
      manufacturer
      type
    }
  }
`;

const ITEM_REQUEST_FIELDS = gql`
  fragment itemDetailFields on Item {
    _id
    number
    nextItemNumber
    previousItemNumber
    pilatNr
    wholesaleNr
    industryNr
    description
    supplier
    configurationState
    unit
    type
    solarPlant
    price
    installationTimeInMinutes
    createdAt
    updatedAt
    deleted
    priceCalculation {
      # Item pricing
      itemMarkup
      itemProfitMarkup
      itemCustomerPrice

      # Labor pricing
      laborCost
      laborMarkup
      laborProfitMarkup
      laborCustomerPrice

      # Total pricing
      totalCustomerPrice

      calculatedAt
    }
    itemAttachments {
      id
      path
      thumbnail
      filename
      displayFilename
      size
      mimetype
    }
    wallbox {
      manufacturer
      type
      maxNetInputPower
      maxNetOutputPower
      minChargingPower
      maxChargingPower
      activePowerControllable
      chargingType
      shiftFactorCosphiType
      connectionType
      connectionTypes
    }
    solarPanel {
      manufacturer
      type
      power
    }
    battery {
      manufacturer
      type
      capacity
      dischargePower
      batteryModuleCount
      batteryModuleCapacity
      usableCapacity
      ratedVoltage
      connectionType
    }
    inverter {
      manufacturer
      type
      nominalPower
      apparentPower
      activePower
      ratedCurrent
      shortCircuitCurrent
      nominalDcPower
      allowedChargeDischargeCurrent
      gridAndPlantProtectionIntegrated
      gridAndPlantProtectionType
      nominalVoltage
    }
    energyFlowDirectionSensor {
      manufacturer
      type
    }
  }
`;

/**
 * sanitize inputs and create payload to send to the graphql api
 * @param {*} item
 */
const createPayload = (item) => {
  const payload = {
    ...{ pilatNr: item.pilatNr },
    ...{ wholesaleNr: item.wholesaleNr },
    ...{ industryNr: item.industryNr },
    ...{ supplier: item.supplier },
    ...{ configurationState: item.configurationState },
    ...(item.description ? { description: item.description } : {}),
    ...(item.unit ? { unit: item.unit } : {}),
    ...(item.type ? { type: item.type } : {}),
    ...(item.solarPlant ? { solarPlant: item.solarPlant } : {}),
    ...(item.price ? { price: parseFloat(item.price) } : { price: 0 }),
    ...(item.installationTimeInMinutes
      ? { installationTimeInMinutes: parseFloat(item.installationTimeInMinutes) }
      : { installationTimeInMinutes: 0 }),
    ...(item.wallbox
      ? {
          wallbox: {
            manufacturer: item.wallbox.manufacturer,
            type: item.wallbox.type,
            maxNetInputPower: parseFloat(item.wallbox.maxNetInputPower),
            maxNetOutputPower: parseFloat(item.wallbox.maxNetOutputPower),
            minChargingPower: parseFloat(item.wallbox.minChargingPower),
            maxChargingPower: parseFloat(item.wallbox.maxChargingPower),
            activePowerControllable: item.wallbox.activePowerControllable,
            chargingType: item.wallbox.chargingType,
            shiftFactorCosphiType: item.wallbox.shiftFactorCosphiType,
            connectionType: item.wallbox.connectionType,
            connectionTypes: item.wallbox.connectionTypes,
          },
        }
      : {}),
    ...(item.solarPanel
      ? {
          solarPanel: {
            manufacturer: item.solarPanel.manufacturer,
            type: item.solarPanel.type,
            power: item.solarPanel.power ? parseFloat(item.solarPanel.power) : undefined,
          },
        }
      : {}),
    ...(item.inverter
      ? {
          inverter: {
            manufacturer: item.inverter.manufacturer,
            type: item.inverter.type,
            nominalPower: parseFloat(item.inverter.nominalPower),
            apparentPower: parseFloat(item.inverter.apparentPower),
            activePower: parseFloat(item.inverter.activePower),
            ratedCurrent: parseFloat(item.inverter.ratedCurrent),
            shortCircuitCurrent: parseFloat(item.inverter.shortCircuitCurrent),
            nominalDcPower: parseFloat(item.inverter.nominalDcPower),
            allowedChargeDischargeCurrent: parseFloat(item.inverter.allowedChargeDischargeCurrent),
            gridAndPlantProtectionIntegrated: parseInt(
              item.inverter.gridAndPlantProtectionIntegrated
            ),
            gridAndPlantProtectionType: item.inverter.gridAndPlantProtectionType,
            nominalVoltage: parseFloat(item.inverter.nominalVoltage),
          },
        }
      : {}),
    ...(item.energyFlowDirectionSensor
      ? {
          energyFlowDirectionSensor: {
            manufacturer: item.energyFlowDirectionSensor.manufacturer,
            type: item.energyFlowDirectionSensor.type,
          },
        }
      : {}),
    ...(item.battery
      ? {
          battery: {
            manufacturer: item.battery.manufacturer,
            type: item.battery.type,
            capacity: parseFloat(item.battery.capacity),
            dischargePower: parseFloat(item.battery.dischargePower),
            batteryModuleCount: parseInt(item.battery.batteryModuleCount),
            batteryModuleCapacity: parseFloat(item.battery.batteryModuleCapacity),
            usableCapacity: parseFloat(item.battery.usableCapacity),
            ratedVoltage: parseFloat(item.battery.ratedVoltage),
            connectionType: item.battery.connectionType,
          },
        }
      : {}),
  };
  return payload;
};

/**
 * Actions
 */
const actions = {
  /**
   * Fetch a paginated, sorted and filtered item list
   * @param {*} param0
   * @param {*} payload
   */
  async fetchItemsPaginated({ commit, dispatch }, { pagination, sorting, filters }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query itemsPaginated(
            $pagination: PaginationInput
            $sorting: SortingInput
            $filters: ItemFilterInput
          ) {
            itemsPaginated(pagination: $pagination, sorting: $sorting, filters: $filters) {
              items {
                ...itemListFields
              }
              itemCount
            }
          }
          ${ITEM_LIST_REQUEST_FIELDS}
        `,
        variables: {
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });
      commit('SET_ITEMS', response.data.itemsPaginated.items);
      commit('SET_ITEM_COUNT', response.data.itemsPaginated.itemCount);
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  async fetchItems({ commit, dispatch }, payload) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            items {
              ...itemListFields
            }
          }
          ${ITEM_LIST_REQUEST_FIELDS}
        `,
      });
      commit('SET_ITEMS', response.data.items);
    } catch (err) {
      throw new Error(err);
    }
  },

  async fetchItemsByType({ commit, dispatch }, itemType) {
    try {
      // console.log(itemNumber);
      const response = await apolloClient.query({
        query: gql`
          query itemsByType($itemType: ItemType!) {
            itemsByType(itemType: $itemType) {
              ...itemListFields
            }
          }
          ${ITEM_LIST_REQUEST_FIELDS}
        `,
        variables: {
          itemType: itemType,
        },
      });
      console.log('Items: ', response.data.itemsByType);
      commit('SET_ITEMS', response.data.itemsByType);
    } catch (err) {
      throw new Error(err);
    }
  },
  async fetchItemsBySolarPlant({ commit, dispatch }, solarPlantType) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query itemsBySolarPlant($itemSolarPlant: ItemSolarPlant!) {
            itemsBySolarPlant(itemSolarPlant: $itemSolarPlant) {
              ...itemListFields
            }
          }
          ${ITEM_LIST_REQUEST_FIELDS}
        `,
        variables: {
          itemSolarPlant: solarPlantType,
        },
      });

      commit('SET_SOLAR_PLANT_ITEMS', response.data.itemsBySolarPlant); // Geändert
    } catch (err) {
      console.error('Error in fetchItemsBySolarPlant:', err);
      throw err;
    }
  },
  async fetchItemByBarcode({ commit, dispatch }, barcode) {
    try {
      // console.log('fetchItemByBarcode()', barcode, typeof barcode);
      const response = await apolloClient.query({
        query: gql`
          query itemByBarcode($barcode: String!) {
            itemByBarcode(barcode: $barcode) {
              ...itemDetailFields
            }
          }
          ${ITEM_REQUEST_FIELDS}
        `,
        variables: {
          barcode: barcode,
        },
      });
      // console.log('fetchItemByBarcode()', response.data.itemByBarcode);
      if (response.data.itemByBarcode) {
        commit('ADD_OR_UPDATE_ITEM', response.data.itemByBarcode);
      }
    } catch (err) {
      throw new Error(err);
    }
  },

  async fetchItemByNumber({ commit, dispatch }, itemNumber) {
    try {
      // console.log(itemNumber);
      const response = await apolloClient.query({
        query: gql`
          query itemByNumber($itemNumber: Int!) {
            itemByNumber(itemNumber: $itemNumber) {
              ...itemDetailFields
            }
          }
          ${ITEM_REQUEST_FIELDS}
        `,
        variables: {
          itemNumber: parseInt(itemNumber),
        },
      });
      commit('ADD_OR_UPDATE_ITEM', response.data.itemByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  async fetchItemUnits({ commit }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query {
            __type(name: "ItemUnit") {
              name
              enumValues {
                name
              }
            }
          }
        `,
      });
      commit('SET_ITEM_UNITS', response.data.__type.enumValues);
    } catch (err) {
      // commit('SET_FLASH_MESSAGE', {message: err.message, type: 'is-danger'})
    }
  },

  async createItem({ commit, dispatch }, item) {
    const payload = createPayload(item);

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation createItem($payload: ItemInput!) {
            createItem(itemInput: $payload) {
              ...itemDetailFields
            }
          }
          ${ITEM_REQUEST_FIELDS}
        `,
        variables: {
          payload: payload,
        },
      });
      dispatch('message', {
        message: 'Material gespeichert!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_ITEM', '_new'); // remove temporary _new item
      commit('ADD_OR_UPDATE_ITEM', response.data.createItem);
      return response.data.createItem.number;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async updateItem({ commit, dispatch }, item) {
    const payload = createPayload(item);
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateItem($itemNumber: Int!, $payload: ItemInput!) {
            updateItem(itemNumber: $itemNumber, itemInput: $payload) {
              ...itemDetailFields
            }
          }
          ${ITEM_REQUEST_FIELDS}
        `,
        variables: {
          itemNumber: item.number,
          payload: payload,
        },
      });
      dispatch('message', {
        message: 'Material aktualisiert!',
        type: 'success',
        lifetime: 2,
      });
      commit('ADD_OR_UPDATE_ITEM', response.data.updateItem);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim bearbeiten! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },
  /**
   * Upload an image belonging to the item
   * @param commit
   * @param payload
   * @returns {Promise<void>}
   */
  async uploadItemAttachment({ commit, dispatch }, payload) {
    console.log(payload);
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation uploadItemAttachment($attachmentInput: AttachmentInput!) {
            uploadItemAttachment(attachmentInput: $attachmentInput) {
              _id
              itemAttachments {
                id
                path
                thumbnail
                filename
                displayFilename
                size
                mimetype
              }
            }
          }
        `,
        variables: {
          attachmentInput: {
            file: payload.file,
            referenceId: payload.parentId,
          },
        },
      });
      commit('UPDATE_ITEM_ATTACHMENTS', {
        updatedItem: response.data.uploadItemAttachment,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Upload!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },
  /**
   * Delete Item Atrachment
   * @param commit
   * @param payload
   */
  async deleteItemAttachment({ commit, dispatch }, payload) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation deleteItemAttachment(
            $itemId: ID!
            $attachmentId: ID!
            $attachmentType: String!
          ) {
            deleteItemAttachment(
              itemId: $itemId
              attachmentId: $attachmentId
              attachmentType: $attachmentType
            ) {
              _id
              itemAttachments {
                id
                path
                thumbnail
                filename
                displayFilename
                size
                mimetype
              }
            }
          }
        `,
        variables: {
          itemId: payload.parentId, // Changed from payload.itemId
          attachmentId: payload.attachmentId,
          attachmentType: payload.attachmentType || 'default', // Provide default value if missing
        },
      });
      commit('UPDATE_ITEM', response.data.deleteItemAttachment);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },
  async deleteItem({ commit, dispatch }, itemNumber) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation deleteItem($itemNumber: Int!) {
            deleteItem(itemNumber: $itemNumber)
          }
        `,
        variables: {
          itemNumber: itemNumber,
        },
      });
      dispatch('message', {
        message: 'Material gelöscht!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_ITEM', itemNumber);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * create a new item object in store and return it
   * @param {*} param0
   * @param {*} item
   */
  async initItem({ commit }) {
    commit('ADD_OR_UPDATE_ITEM', defaultItem());
  },

  itemLocalUpdate({ commit }, item) {
    commit('ADD_OR_UPDATE_ITEM', item);
  },

  itemCleanup({ commit }) {
    commit('REMOVE_ITEM', '_new');
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_ITEM_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_ITEMS: (state, items) => (items ? (state.items = items) : (state.items = [])),
  ADD_ITEM: (state, item) => state.items.push(item),
  ADD_OR_UPDATE_ITEM: (state, updatedItem) => {
    const existingItem = state.items.find((item) => item._id === updatedItem._id);
    if (existingItem) {
      Object.assign(existingItem, updatedItem);
    } else {
      state.items.push(updatedItem);
    }
  },
  REMOVE_ITEM: (state, itemId) => {
    const index = state.items.findIndex((item) => item._id === itemId);
    state.items.splice(index, 1);
  },
  UPDATE_ITEM: (state, updatedItem) => {
    const item = state.items.find((item) => item._id === updatedItem._id);
    Object.assign(item, updatedItem);
  },
  SET_ITEM_UNITS: (state, itemUnits) => {
    state.itemUnits = [];
    itemUnits.map((itemUnit) => {
      state.itemUnits.push(itemUnit.name);
    });
  },
  SET_SOLAR_PLANT_ITEMS: (state, items) => {
    state.solarPlantItems = items || [];
  },
  SET_ITEM_COUNT: (state, itemCount) => (state.itemCount = itemCount),
  UPDATE_ITEM_ATTACHMENTS: (state, { updatedItem }) => {
    const item = state.items.find((item) => item._id === updatedItem._id);
    if (item) {
      Object.keys(updatedItem).forEach((key) => {
        Vue.set(item, key, updatedItem[key]);
      });
    }
  },
};

/**
 * Getters
 */
const getters = {
  getItems: (state) => {
    return state.items.map((item) => ({
      ...item,
      addRemoveCount: 1,
    }));
  },
  getItem: (state) => (itemId) => state.items.find((item) => item._id == itemId),
  getItemsByType: (state) => (itemType) => state.items.filter((item) => item.type == itemType),
  getItemByNumber: (state) => (itemNumber) => state.items.find((item) => item.number == itemNumber),
  getItemsBySolarPlant: (state) => (solarPlantType) =>
    state.solarPlantItems.filter((item) => item.solarPlant == solarPlantType),
  getItemByBarcode: (state) => (barcode) => {
    const item = state.items.find(
      (item) => item.wholesaleNr == barcode || item.industryNr == barcode
    );
    if (item) {
      Object.assign(item, { addRemoveCount: 1 });
    }

    return item;
  },
  getItemUnits: (state) => state.itemUnits,
  getItemCount: (state) => state.itemCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
